import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import StatusPill from '../components/StatusPill';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';
import { offerForceRetry } from '../components/forceRetry';
import { waitingFor } from '../format';

/**
 * Home — the operational dashboard from the prototype.
 *
 * **Every number on this page is real.** The prototype fills its KPI cards with
 * invented figures, which is right for a mockup and wrong for an admin: a
 * plausible number on a dashboard gets acted on, and one nobody computed is
 * worse than no number at all. So each card is derived from an endpoint that
 * exists:
 *
 *   - signups today, this week — `searchSignups` with `resultsPerPage: 1`, whose
 *     `pages` is then the row count. One cheap request per figure, and exact.
 *   - bookings and cancellations waiting — the same `getOverview` payload the
 *     Overview page reads, counted.
 *   - failures among them — counted from the same payload.
 *
 * The prototype's fourth card is revenue over 24h, and for a long time it was
 * simply absent: there was no endpoint, and inventing one — or wiring the card
 * to something that merely looked like money — is the one thing a dashboard must
 * not do. `transaction/settled-summary` now answers it (Api.Wannasport#1163),
 * and the card is here with the label the data supports: **settled**, not
 * revenue. Revenue is an accounting question; what settled in the last 24 hours
 * is a fact. Currencies are never added together, refunds are shown next to the
 * gross rather than folded into it, and authorisations are not counted, because
 * an authorisation is money that may yet not arrive.
 *
 * The rest follows the design: the failures table with its actions, the
 * cancellations panel with the empty state, and recent activity — which is built
 * from the overview rows rather than an audit log, because there isn't one.
 */

const formatDate = value => (value ? String(value).replace('T', ' ').slice(0, 16) : '—');

// Whole units: on a dashboard the øre are noise, and a figure that fits is a
// figure that gets read.
const formatMoney = (amount, currency) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value).toLocaleString()} ${currency || ''}`.trim();
};

/**
 * The line under the amount. It says what the figure is *not* — the gross, and
 * how much came back out — because "settled minus refunds" on its own leaves
 * somebody to guess which of the two they are looking at.
 */
const moneyHint = rows => rows
  .map(row => {
    const currency = row.currencyIsoCode ?? row.CurrencyIsoCode;
    const settled = Number(row.settledAmount ?? row.SettledAmount) || 0;
    const refunds = Number(row.refundAmount ?? row.RefundAmount) || 0;
    const count = row.settledCount ?? row.SettledCount ?? 0;

    const gross = `${count} settled · ${formatMoney(settled, currency)}`;
    // The magnitude, not the stored sign: "−1,210 DKK refunded" says the
    // direction twice and reads as money going the wrong way twice.
    return refunds ? `${gross}, ${formatMoney(Math.abs(refunds), currency)} refunded` : gross;
  })
  .join(' · ');

const camelToWords = text => {
  const value = text == null ? '' : String(text);
  const spaced = value.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const startOfToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T00:00`;
};

const startOfWeek = () => {
  const now = new Date();
  now.setDate(now.getDate() - 7);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T00:00`;
};

const HomeInternal = ({ api }) => {
  const { ask, modalProps } = useModal();
  const [overview, setOverview] = useState(null);
  const [counts, setCounts] = useState({ today: null, week: null });
  const [money, setMoney] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);

  // `pages` with one row per page is the total. Cheaper than pulling every row
  // back just to call `.length` on it.
  const countSince = useCallback(async signupFrom => {
    try {
      const data = await api.signups.searchSignups({
        filters: { signupFrom, resultsPerPage: 1 }
      });
      return typeof data?.pages === 'number' ? data.pages : null;
    } catch (error) {
      return null;
    }
  }, [api]);

  const load = useCallback(async () => {
    setFailed(false);

    // Its own request, deliberately not awaited with the rest: one card must not
    // be able to hold up the page it sits on, and the api function answers `[]`
    // rather than throwing.
    api.transactions.getSettledSummary(24).then(setMoney).catch(() => setMoney([]));

    try {
      const data = await api.overview.getOverview();
      // The api module wraps the body in `{ success, data }`, so `data.data` is
      // the list — but only if the endpoint answered with one. Anything else
      // becomes an empty dashboard rather than a `rows.filter is not a function`
      // that takes the whole page down.
      setOverview(Array.isArray(data?.data) ? data.data : []);
      setLoadedAt(new Date());
    } catch (error) {
      setFailed(true);
      return;
    }

    const [today, week] = await Promise.all([countSince(startOfToday()), countSince(startOfWeek())]);
    setCounts({ today, week });
  }, [api, countSince]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (tone, text) => setNote({ tone, text });

  const act = async (fn, okText, failText) => {
    setBusy(true);
    try {
      const result = await fn();
      const ok = result?.success !== false;
      say(ok ? 'info' : 'error', ok ? okText : (result?.message || failText));
      if (ok) await load();
      // Rethrown so the dialog's busy state ends on the failure rather than
      // looking like it worked.
      if (!ok) throw new Error(failText);
    } finally {
      setBusy(false);
    }
  };

  const ctx = row => `signup …${String(row.id).slice(-6)}`;

  const retry = (row, isBooking) => {
    const what = isBooking ? 'booking' : 'cancellation';
    const already = (isBooking ? row.bookStatus : row.cancellationStatus) === 'Success';

    ask({
      kind: already ? 'danger' : 'confirm',
      title: already ? `Retry a ${what} that already succeeded?` : `Retry this ${what}?`,
      context: `Retry ${what} · ${ctx(row)}`,
      body: already
        ? `This ${what} already came back Success. Sending it again asks the provider for the same slot a second time.`
        : 'A fresh request goes to the external system with the same activity and court.',
      warning: already
        ? 'A second successful booking is not undone by this admin — you would have to cancel one by hand at the venue.'
        : undefined,
      confirmLabel: already ? 'Retry anyway' : `Retry ${what}`,
      onConfirm: () => act(
        async () => {
          const result = await api.overview.retry(row.id, isBooking, false);
          if (result?.canForce) {
            offerForceRetry(ask, {
              what,
              context: `Retry ${what} · ${ctx(row)}`,
              message: result.message,
              onConfirm: () => act(
                () => api.overview.retry(row.id, isBooking, true),
                'Put back in the queue.',
                'Unable to put it back in the queue.'
              )
            });
          }
          return result;
        },
        'Put back in the queue.',
        'Unable to put it back in the queue.'
      )
    });
  };

  const confirmEmail = (row, isBooking) => ask({
    kind: 'confirm',
    title: `Send the ${isBooking ? 'booking' : 'cancellation'} confirmation?`,
    context: `Confirm ${isBooking ? 'booking' : 'cancellation'} · ${ctx(row)}`,
    body: 'The confirmation email is sent to the user at the address on the signup.',
    confirmLabel: 'Send email',
    onConfirm: () => act(
      () => api.overview.sendConfirmation(row.id, isBooking),
      'Confirmation sent.',
      'Unable to send the confirmation.'
    )
  });

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not load the dashboard.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const rows = overview ?? [];
  const bookings = rows.filter(({ showInSignups }) => showInSignups);
  const cancellations = rows.filter(({ showInCancellation }) => showInCancellation);
  const failures = bookings.filter(({ bookStatus }) => bookStatus === 'Failure').length;

  const kpis = [
    {
      label: 'Signups today',
      value: counts.today,
      hint: 'since midnight, all facilities'
    },
    {
      label: 'Signups, 7 days',
      value: counts.week,
      hint: 'rolling week'
    },
    {
      label: 'Bookings waiting',
      value: overview === null ? null : bookings.length,
      hint: failures > 0 ? `${failures} failed outright` : 'none failed outright',
      tone: failures > 0 ? 'bad' : null
    },
    {
      label: 'Cancellations waiting',
      value: overview === null ? null : cancellations.length,
      hint: 'did not complete on their own',
      tone: cancellations.length > 0 ? 'warn' : null
    },
    {
      label: 'Settled, 24 hours',
      // One currency is the normal case and gets the amount; more than one gets
      // the count, because there is no honest single number across them and a
      // card is not the place to show three.
      text: money === null
        ? null
        : (money.length === 0
          ? 'nothing'
          : (money.length === 1
            ? formatMoney(money[0].netAmount ?? money[0].NetAmount, money[0].currencyIsoCode ?? money[0].CurrencyIsoCode)
            : `${money.length} currencies`)),
      hint: money === null || money.length === 0
        ? 'settled minus refunds'
        : moneyHint(money)
    }
  ];

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Home</h1>
          <p className="next-muted">
            {loadedAt
              ? `Operational state · loaded at ${loadedAt.toTimeString().slice(0, 5)}`
              : 'Loading…'}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn next-btn--primary" disabled={busy} onClick={load}>
            Refresh
          </button>
        </div>
      </header>

      <Modal {...modalProps} />

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-kpis">
        {kpis.map(({ label, value, text, hint, tone }) => (
          <div className="next-kpi" key={label}>
            <span className="next-kpi__label">{label}</span>
            {/* A money card carries a formatted string, a count carries a
                number. Both render as the one big figure. */}
            <span className={`next-kpi__value${text && text.length > 12 ? ' next-kpi__value--long' : ''}`}>
              {text !== undefined
                ? (text === null ? <span className="next-kpi__pending">—</span> : text)
                : (value === null || value === undefined
                  ? <span className="next-kpi__pending">—</span>
                  : value.toLocaleString())}
            </span>
            <span className={`next-kpi__hint${tone ? ` next-kpi__hint--${tone}` : ''}`}>{hint}</span>
          </div>
        ))}
      </div>

      <section className="next-card">
        <div className="next-card__head">
          <h2>Bookings to handle</h2>
          {failures > 0 && <span className="next-pill next-pill--bad">{failures} failure{failures === 1 ? '' : 's'}</span>}
          <span className="next-card__spacer" />
          <Link className="next-link" to="/next/signups">All signups →</Link>
        </div>

        {overview === null ? (
          <p className="next-table__note next-muted">Loading…</p>
        ) : bookings.length === 0 ? (
          <Empty title="Nothing waiting" body="Every booking completed on its own." />
        ) : (
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Facility</th>
                  <th>User</th>
                  <th>Activity</th>
                  <th>Result</th>
                  <th className="next-table__right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.slice(0, 8).map(row => (
                  <tr key={row.id}>
                    <td data-label=""><Guid value={row.id} to={`/next/signup/${row.id}`} /></td>
                    <td data-label="Facility">
                      <span className="next-stack">
                        <span className="next-stack__main">{row.provider || row.facilityName}</span>
                        <span className="next-stack__sub">{[row.sport, row.title].filter(Boolean).join(' · ')}</span>
                      </span>
                    </td>
                    <td data-label="User" className="next-break">{row.userName}</td>
                    <td data-label="Activity" className="next-nowrap">{formatDate(row.activityDateCet)}</td>
                    <td data-label="Result"><StatusPill status={row.bookStatus}>{camelToWords(row.bookStatus)}</StatusPill></td>
                    <td className="next-table__right" data-label="">
                      <div className="next-actions next-actions--end">
                        <button type="button" className="next-btn next-btn--sm next-btn--primary" disabled={busy} onClick={() => retry(row, true)}>Retry</button>
                        <button type="button" className="next-btn next-btn--sm" disabled={busy} onClick={() => confirmEmail(row, true)}>Confirm</button>
                        <Link className="next-btn next-btn--sm" to={`/next/signup/${row.id}`}>···</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bookings.length > 8 && (
              <div className="next-pager">
                <span className="next-muted">
                  Showing 8 of {bookings.length}
                </span>
                <span className="next-pager__spacer" />
                <Link className="next-btn next-btn--sm" to="/next/overview">See all</Link>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="next-split">
        <section className="next-card">
          <div className="next-card__head">
            <h2>Cancellations</h2>
            <span className="next-card__spacer" />
            <span className="next-muted">waiting</span>
          </div>

          {overview === null ? (
            <p className="next-table__note next-muted">Loading…</p>
          ) : cancellations.length === 0 ? (
            <Empty title="No cancellation waiting" body="Every cancellation completed on its own." />
          ) : (
            <div className="next-table-wrap">
              <table className="next-table next-table--wide next-table--stack">
                <thead>
                  <tr>
                    <th>Booking</th>
                    <th>Facility</th>
                    <th>Result</th>
                    <th className="next-table__right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.slice(0, 6).map(row => (
                    <tr key={row.id}>
                      <td data-label=""><Guid value={row.id} to={`/next/signup/${row.id}`} /></td>
                      <td data-label="Facility">{row.provider || row.facilityName}</td>
                      <td data-label="Result"><StatusPill status={row.cancellationStatus}>{camelToWords(row.cancellationStatus)}</StatusPill></td>
                      <td className="next-table__right" data-label="">
                        <div className="next-actions next-actions--end">
                          <button type="button" className="next-btn next-btn--sm" disabled={busy} onClick={() => retry(row, false)}>Retry</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="next-card">
          <div className="next-card__head"><h2>Needs attention first</h2></div>
          {/*
            The prototype calls this "recent activity" and fills it from an audit
            log. There is no audit log, so rather than fabricate a feed this shows
            the oldest waiting items — which is the question the panel was there to
            answer anyway: what has been sitting unhandled longest.
          */}
          <div className="next-feed">
            {overview === null && <span className="next-muted">Loading…</span>}
            {overview !== null && rows.length === 0 && (
              <span className="next-muted">Nothing waiting.</span>
            )}
            {[...rows]
              .sort((a, b) => String(a.signupDateCet || '').localeCompare(String(b.signupDateCet || '')))
              .slice(0, 5)
              .map(row => (
                <Link className="next-feed__row" key={row.id} to={`/next/signup/${row.id}`}>
                  <span
                    className={`next-feed__dot next-feed__dot--${
                      row.bookStatus === 'Failure' || row.cancellationStatus === 'Failure' ? 'bad' : 'warn'
                    }`}
                  />
                  <span className="next-feed__body">
                    <span className="next-feed__text">
                      {row.provider || row.facilityName} · {row.sport}
                    </span>
                    <span className="next-feed__time">
                      {/* The age, not the timestamp: this panel is sorted by
                          how long something has been sitting, and a date is the
                          raw fact rather than the reading. The timestamp stays
                          in the tooltip for anyone who needs the exact moment. */}
                      <span title={formatDate(row.signupDateCet)}>
                        {waitingFor(row.signupDateCet)
                          ? `waiting ${waitingFor(row.signupDateCet)}`
                          : `signed up ${formatDate(row.signupDateCet)}`}
                      </span>
                    </span>
                  </span>
                </Link>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
};

function Empty({ title, body }) {
  return (
    <div className="next-empty-state">
      <span className="next-empty-state__mark" aria-hidden>✓</span>
      <span className="next-empty-state__title">{title}</span>
      <span className="next-empty-state__body">{body}</span>
    </div>
  );
}

Empty.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired
};

HomeInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Home = withApi(HomeInternal);

Home.requiresLogin = true;

export default Home;
